import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import getAgreementsAndVehicle from 'api/agreements/getAgreementsAndVehicle';
import { getPassingsFilterAgreements, getPassingsFilterOBEs, getPassingsFilterPagination } from 'api/passings/getFilteredPassings';
import MultipleSelect from 'components/Form/MultipleSelect';
import PeriodFilter from 'components/PeriodFilter/PeriodFilter';
import Spinner from 'components/Spinner/Spinner';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import flatten from 'utils/general/flatArray';
import checkLoadable from 'utils/recoil/checkLoadable';
import useStyles from './PassingStyles';
export default function PassingsFilterForm() {
  const {
    column,
    selectionFields,
    periodFilter
  } = useStyles();
  const {
    customer
  } = useCurrentCustomer();
  const agreementsLoadable = useRecoilValueLoadable(getAgreementsAndVehicle(customer?.id));
  const {
    contents: agreements,
    loading
  } = checkLoadable(agreementsLoadable);
  const {
    t
  } = useTranslation();
  const [selectedAgreements, setSelectedAgreements] = useRecoilState(getPassingsFilterAgreements);
  const [selectedObes, setSelectedObes] = useRecoilState(getPassingsFilterOBEs);
  const setPagination = useSetRecoilState(getPassingsFilterPagination);
  const possibleObes = useMemo(() => flatten(agreements?.filter(a => selectedAgreements.includes(a.id ?? '')).map(a => a.obes?.map(o => ({
    label: o.vehicle ? `${o.vehicle.registrationNumber} (${o.obeNumber})` : o.obeNumber,
    value: o.obeNumber
  }))) ?? []), [agreements, selectedAgreements]);
  useEffect(() => {
    setSelectedObes(obes => obes.filter(obe => possibleObes.some(po => po.value === obe)));
  }, [setSelectedObes, possibleObes]);
  useEffect(() => {
    setSelectedAgreements(agreements?.map(a => a.id ?? '') ?? []);
    setSelectedObes(flatten(agreements?.map(a => a.obes?.map(o => o.obeNumber)) ?? []));
  }, [agreements, setSelectedAgreements, setSelectedObes]);
  const handlePeriodFilterChange = ({
    startDate,
    endDate
  }) => {
    setPagination(currentValue => ({
      ...currentValue,
      dateFrom: startDate?.toDate(),
      dateTo: endDate?.toDate()
    }));
  };
  if (loading) return /*#__PURE__*/React.createElement(Spinner, null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: selectionFields
  }, /*#__PURE__*/React.createElement("div", {
    className: column
  }, /*#__PURE__*/React.createElement(MultipleSelect, {
    items: agreements?.map(a => ({
      value: a.id || '',
      label: `${a.name} (${a.agreementNumber})`
    })) ?? [],
    id: "passings-filter-agreements",
    label: t('passings.select_agreements'),
    onChange: v => setSelectedAgreements(v),
    defaultItems: selectedAgreements
  })), /*#__PURE__*/React.createElement("div", {
    className: column
  }, /*#__PURE__*/React.createElement(MultipleSelect, {
    items: possibleObes,
    id: "passings-filter-obes",
    label: t('passings.select_obes'),
    onChange: v => setSelectedObes(v),
    defaultItems: selectedObes
  }))), /*#__PURE__*/React.createElement("div", {
    className: periodFilter
  }, /*#__PURE__*/React.createElement(PeriodFilter, {
    onChange: handlePeriodFilterChange,
    useUtc: true,
    inline: true
  })));
}